import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface UploadProgress {
  [key: string]: number;
}

export interface UploadResult {
  success: boolean;
  url?: string;
  path?: string;
  error?: string;
}

interface UseAuthenticatedUploadOptions {
  bucket: string;
  onSessionExpired?: () => void;
}

/**
 * A reusable hook for handling authenticated file uploads with session token management
 * and session expiry detection across all file upload components.
 */
export const useAuthenticatedUpload = ({ bucket, onSessionExpired }: UseAuthenticatedUploadOptions) => {
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
  const [currentUploadingFile, setCurrentUploadingFile] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  /**
   * Handle session expiry with a friendly message and auto-redirect
   */
  const handleSessionExpiry = useCallback(() => {
    toast.error("Your session has expired. Please sign in again.", {
      duration: 4000,
      action: {
        label: "Sign In",
        onClick: () => window.location.href = "/auth"
      }
    });
    
    // Call custom handler if provided
    if (onSessionExpired) {
      onSessionExpired();
    }
    
    // Auto-redirect after a short delay
    setTimeout(() => {
      window.location.href = "/auth";
    }, 2000);
  }, [onSessionExpired]);

  /**
   * Check if an error is related to session expiry
   */
  const isSessionExpiredError = useCallback((error: any): boolean => {
    if (!error) return false;
    
    return (
      error.isAuthError ||
      error.message === 'SESSION_EXPIRED' ||
      error.code === 'refresh_token_not_found' ||
      error.message?.includes('Refresh Token') ||
      error.message?.includes('Invalid Refresh Token') ||
      error.__isAuthError ||
      error.status === 401 ||
      error.status === 403
    );
  }, []);

  /**
   * Get the current session access token
   * Returns null and triggers session expiry handling if no valid session
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session?.access_token) {
        handleSessionExpiry();
        return null;
      }
      
      return session.access_token;
    } catch (error: any) {
      if (isSessionExpiredError(error)) {
        handleSessionExpiry();
      }
      return null;
    }
  }, [handleSessionExpiry, isSessionExpiredError]);

  /**
   * Upload a file with progress tracking using XMLHttpRequest
   * Uses the user's session token for authenticated uploads
   */
  const uploadFileWithProgress = useCallback(async (
    file: File,
    filePath: string
  ): Promise<UploadResult> => {
    const accessToken = await getAccessToken();
    
    if (!accessToken) {
      return { success: false, error: 'SESSION_EXPIRED' };
    }

    return new Promise((resolve) => {
      const fileKey = `${file.name}-${file.size}`;
      setCurrentUploadingFile(file.name);
      setUploadProgress(prev => ({ ...prev, [fileKey]: 0 }));
      setIsUploading(true);

      const xhr = new XMLHttpRequest();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(prev => ({ ...prev, [fileKey]: percentComplete }));
        }
      });

      xhr.addEventListener('load', () => {
        setIsUploading(false);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          setUploadProgress(prev => ({ ...prev, [fileKey]: 100 }));
          resolve({ success: true, path: filePath });
        } else if (xhr.status === 401 || xhr.status === 403) {
          handleSessionExpiry();
          resolve({ success: false, error: 'SESSION_EXPIRED' });
        } else {
          resolve({ success: false, error: `Upload failed: ${xhr.statusText}` });
        }
      });

      xhr.addEventListener('error', () => {
        setIsUploading(false);
        resolve({ success: false, error: 'Upload failed' });
      });

      xhr.open('POST', `${supabaseUrl}/storage/v1/object/${bucket}/${filePath}`);
      xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
      xhr.setRequestHeader('x-upsert', 'true');
      xhr.send(file);
    });
  }, [bucket, getAccessToken, handleSessionExpiry]);

  /**
   * Upload a file using the Supabase SDK (simpler, no progress tracking)
   * Handles session expiry automatically
   * Note: For private buckets, use createSignedUrl when displaying the file
   */
  const uploadFile = useCallback(async (
    file: File,
    filePath: string,
    options?: { isPublicBucket?: boolean }
  ): Promise<UploadResult> => {
    setIsUploading(true);
    setCurrentUploadingFile(file.name);

    try {
      // First verify session is valid
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setIsUploading(false);
        return { success: false, error: 'SESSION_EXPIRED' };
      }

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file);

      if (uploadError) {
        if (isSessionExpiredError(uploadError)) {
          handleSessionExpiry();
          return { success: false, error: 'SESSION_EXPIRED' };
        }
        throw uploadError;
      }

      // For public buckets, return a public URL
      // For private buckets, return path only (use createSignedUrl when displaying)
      let url: string | undefined;
      if (options?.isPublicBucket) {
        const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
        url = data.publicUrl;
      }

      return { success: true, url, path: filePath };
    } catch (error: any) {
      if (isSessionExpiredError(error)) {
        handleSessionExpiry();
        return { success: false, error: 'SESSION_EXPIRED' };
      }
      return { success: false, error: error.message || 'Upload failed' };
    } finally {
      setIsUploading(false);
      setCurrentUploadingFile(null);
    }
  }, [bucket, getAccessToken, handleSessionExpiry, isSessionExpiredError]);

  /**
   * Create a signed URL for accessing files in private buckets
   * @param filePath - The path of the file in storage
   * @param expiresIn - Seconds until the URL expires (default: 3600 = 1 hour)
   */
  const createSignedUrl = useCallback(async (
    filePath: string,
    expiresIn: number = 3600
  ): Promise<string | null> => {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(filePath, expiresIn);
      
      if (error) throw error;
      return data.signedUrl;
    } catch (error) {
      console.error('Failed to create signed URL:', error);
      return null;
    }
  }, [bucket]);

  /**
   * Get the public URL for an uploaded file
   */
  const getPublicUrl = useCallback((filePath: string): string => {
    const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
    return data.publicUrl;
  }, [bucket]);

  /**
   * Reset upload state
   */
  const resetUploadState = useCallback(() => {
    setUploadProgress({});
    setCurrentUploadingFile(null);
    setIsUploading(false);
  }, []);

  /**
   * Handle errors from any async operation, checking for session expiry
   */
  const handleError = useCallback((error: any, defaultMessage: string = "An error occurred") => {
    if (isSessionExpiredError(error)) {
      handleSessionExpiry();
      return;
    }
    toast.error(error.message || defaultMessage);
  }, [handleSessionExpiry, isSessionExpiredError]);

  return {
    // State
    uploadProgress,
    currentUploadingFile,
    isUploading,
    
    // Methods
    uploadFile,
    uploadFileWithProgress,
    getPublicUrl,
    createSignedUrl,
    getAccessToken,
    resetUploadState,
    handleError,
    handleSessionExpiry,
    isSessionExpiredError,
    
    // State setters for custom usage
    setUploadProgress,
    setCurrentUploadingFile,
  };
};
